import type { Command, JsonValue, RecordDefinition, Resource, Transaction } from "@e308/core";
import { armyAllocationCommand } from "./army-allocation.js";
import { rejectUnlessActiveBorder } from "./border-guards.js";
import { rejectIfEliminated } from "./command-guards.js";
import type { SeatModel } from "./content-types.js";
import { formatMicro, type Micro, microFromString } from "./exact-numbers.js";
import type { LockedMatchSetup } from "./lifecycle-state.js";
import type { MatchLifecycleState } from "./lifecycle-types.js";
import {
  cancelAutoReinforce,
  cancelTransit,
  enqueueAutoReinforce,
  reorderAutoReinforce,
  setStandingOrder,
  startTravel,
  subtractTroops,
} from "./logistics-state.js";
import type { LogisticsState, TroopComposition } from "./logistics-types.js";
import { UNIT_IDS } from "./logistics-types.js";
import { reinforcementCommandFor } from "./reinforcement-commands.js";
import { effectMultiplier, effectsForResearch } from "./research-effects.js";
import type { ConflictIntent } from "./types.js";

type LogisticsIntent = Extract<
  ConflictIntent,
  | {
      readonly kind:
        | "allocate-army"
        | "adjust-army"
        | "route-production"
        | "set-dispatch-mode"
        | "launch-wave"
        | "release-wave"
        | "set-wave-interval";
    }
  | { readonly kind: "deploy-troops" | "return-troops" | "redeploy-troops" }
  | { readonly kind: "cancel-transit" | "set-standing-order" }
  | {
      readonly kind: "enqueue-auto-reinforce" | "cancel-auto-reinforce" | "reorder-auto-reinforce";
    }
>;

export function isLogisticsIntent(intent: ConflictIntent): intent is LogisticsIntent {
  return [
    "allocate-army",
    "adjust-army",
    "deploy-troops",
    "route-production",
    "set-dispatch-mode",
    "launch-wave",
    "release-wave",
    "set-wave-interval",
    "return-troops",
    "redeploy-troops",
    "cancel-transit",
    "set-standing-order",
    "enqueue-auto-reinforce",
    "cancel-auto-reinforce",
    "reorder-auto-reinforce",
  ].includes(intent.kind);
}

export function logisticsCommandFor(
  model: SeatModel,
  intent: LogisticsIntent,
  stepMs: number,
  seatById: ReadonlyMap<string, SeatModel>,
  lifecycle: RecordDefinition<MatchLifecycleState & JsonValue>,
  setup: LockedMatchSetup,
): Command<Micro> {
  if (intent.kind === "allocate-army" || intent.kind === "adjust-army")
    return armyAllocationCommand(model, intent, seatById, lifecycle);
  if (
    intent.kind === "route-production" ||
    intent.kind === "set-dispatch-mode" ||
    intent.kind === "launch-wave" ||
    intent.kind === "release-wave" ||
    intent.kind === "set-wave-interval"
  )
    return reinforcementCommandFor(model, intent, stepMs, seatById, lifecycle);
  if (intent.kind === "deploy-troops")
    return travelCommand(
      model,
      "home",
      intent.destination,
      intent.units,
      stepMs,
      seatById,
      lifecycle,
    );
  if (intent.kind === "return-troops")
    return travelCommand(model, intent.origin, "home", intent.units, stepMs, seatById, lifecycle);
  if (intent.kind === "redeploy-troops")
    return travelCommand(
      model,
      intent.origin,
      intent.destination,
      intent.units,
      stepMs,
      seatById,
      lifecycle,
    );
  if (intent.kind === "cancel-transit")
    return logisticsCommand(model, (state) => cancelTransit(state, intent.orderId));
  if (intent.kind === "set-standing-order")
    return logisticsCommand(model, (state, transaction) => {
      const target = seatById.get(intent.borderId);
      if (!target) transaction.reject({ code: "invalid-target", id: intent.borderId });
      rejectUnlessActiveBorder(transaction, model, target as SeatModel, lifecycle);
      if (
        intent.order !== "hold" &&
        setup.teamBySeat[model.seat.id] === setup.teamBySeat[intent.borderId]
      )
        transaction.reject({ code: "invalid-target", id: intent.borderId });
      return setStandingOrder(state, intent.borderId, intent.order);
    });
  if (intent.kind === "enqueue-auto-reinforce")
    return logisticsCommand(model, (state, transaction) =>
      enqueueAutoReinforce(
        state,
        liveDestination(transaction, model, intent.destination, seatById, lifecycle),
        intent.desired,
      ),
    );
  if (intent.kind === "cancel-auto-reinforce")
    return logisticsCommand(model, (state) => cancelAutoReinforce(state, intent.orderId));
  return logisticsCommand(model, (state) => reorderAutoReinforce(state, intent.orderIds));
}

function travelCommand(
  model: SeatModel,
  origin: string,
  destination: string,
  units: TroopComposition,
  stepMs: number,
  seatById: ReadonlyMap<string, SeatModel>,
  lifecycle: RecordDefinition<MatchLifecycleState & JsonValue>,
): Command<Micro> {
  return {
    id: `intent:${model.seat.id}:travel`,
    execute: (transaction) => {
      rejectIfEliminated(transaction, model);
      if (destination !== "home")
        liveDestination(transaction, model, destination, seatById, lifecycle);
      if (origin !== "home") {
        const target = seatById.get(origin);
        if (!target) transaction.reject({ code: "invalid-target", id: origin });
        rejectUnlessActiveBorder(transaction, model, target as SeatModel, lifecycle);
      }
      if (origin === "home")
        setHome(transaction, model, subtractTroops(home(transaction, model), units));
      const multiplier = effectMultiplier(
        effectsForResearch(transaction.getRecord(model.research)),
        "military-travel-time",
      );
      transaction.setRecord(
        model.logistics,
        startTravel(
          transaction.getRecord(model.logistics),
          origin,
          destination,
          units,
          transaction.gameTimeMs(),
          multiplier,
          stepMs,
        ),
      );
    },
  };
}

function logisticsCommand(
  model: SeatModel,
  update: (state: LogisticsState, transaction: Transaction<Micro>) => LogisticsState & JsonValue,
): Command<Micro> {
  return {
    id: `intent:${model.seat.id}:logistics`,
    execute: (transaction) => {
      rejectIfEliminated(transaction, model);
      transaction.setRecord(
        model.logistics,
        update(transaction.getRecord(model.logistics), transaction),
      );
    },
  };
}

function liveDestination(
  transaction: Transaction<Micro>,
  model: SeatModel,
  destination: string,
  seatById: ReadonlyMap<string, SeatModel>,
  lifecycle: RecordDefinition<MatchLifecycleState & JsonValue>,
): string {
  const target = seatById.get(destination);
  if (!target) transaction.reject({ code: "invalid-target", id: destination });
  rejectUnlessActiveBorder(transaction, model, target as SeatModel, lifecycle);
  return destination;
}

function home(transaction: Transaction<Micro>, model: SeatModel): TroopComposition {
  return Object.freeze(
    Object.fromEntries(
      UNIT_IDS.map((id) => [id, formatMicro(transaction.get(unitResource(model, id)))]),
    ),
  ) as TroopComposition;
}

function setHome(
  transaction: Transaction<Micro>,
  model: SeatModel,
  troops: TroopComposition,
): void {
  for (const id of UNIT_IDS) transaction.set(unitResource(model, id), microFromString(troops[id]));
}

function unitResource(model: SeatModel, id: (typeof UNIT_IDS)[number]): Resource<Micro> {
  const key = {
    "guard-robot": "guardRobot",
    "raider-robot": "raiderRobot",
    "siege-gun": "siegeGun",
  } as const;
  return model.resources[key[id]];
}
